import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  ProjectErrors,
  TProjectDataWithoutRepository,
  initialProjectData
} from "@/zapi-frontend/widgets/CreateProjectForm";
import { TModelType } from "@/zapi-frontend/shared/types/models";
import { useState } from "react";
import { LS_CREATE_PROJECT_STORAGE } from "../shared/local-storage/keys";

export type TBusinessModel = {
  id?: number;
  modelArchitecture: string;
  productType: string;
  transactionModel: string;
  customerType: string;
  industrySegment?: string;
  textualDescription: string;
  confidence?: number;
};

export type TDocumentSelection = {
  documentId: number;
  selected: boolean;
  contentType: "FULL" | "SUMMARIZED";
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { repository, ...initialProjectDataWithoutRepository } = initialProjectData;

export { initialProjectDataWithoutRepository };

// The name and description the page last filled in itself; a field still holding them was never
// edited by the user, so a newer suggestion may replace it.
export type TGeneratedProjectMeta = { name: string; description: string };

// What the documents suggest for Section 1's other fields, asked one by one after the decision. Kept
// per draft so a walkthrough cut off by leaving the page can ask the rest on return.
export type TDecisionFieldSuggestions = { format: string | null; scope: string | null; intent: string | null };

type TCreateProjectState = {
  projectDataByCaseBrief: Record<number, TProjectDataWithoutRepository>;
  generatedMetaByCaseBrief: Record<number, TGeneratedProjectMeta>;
  decisionFieldSuggestionsByCaseBrief: Record<number, TDecisionFieldSuggestions>;
  // Where the Case Frame walkthrough is, as JSON the chat writes and reads back: the questions still
  // to ask, the sections already shown, the answers so far. Kept per draft so leaving the page or
  // reloading it resumes the walkthrough instead of losing it.
  walkthroughByCaseBrief: Record<number, string>;
  launchNudgeSeenByCaseBrief: Record<number, true>;
  createdProjectId: number | null;
  prefillOptions: { model: TModelType | null };
  shouldPrefill: boolean;
  errors: ProjectErrors;
  isLoading: boolean;
  processingCompleted: boolean;
  documentSelections: TDocumentSelection[];

  setProjectData: (caseBriefId: number, data: TProjectDataWithoutRepository) => void;
  updateProjectData: (caseBriefId: number, updates: Partial<TProjectDataWithoutRepository>) => void;
  clearProjectData: (caseBriefId: number) => void;
  setGeneratedMeta: (caseBriefId: number, meta: TGeneratedProjectMeta) => void;
  setDecisionFieldSuggestions: (caseBriefId: number, suggestions: TDecisionFieldSuggestions) => void;
  setWalkthrough: (caseBriefId: number, walkthrough: string | null) => void;
  markLaunchNudgeSeen: (caseBriefId: number) => void;
  setCreatedProjectId: (id: number | null) => void;
  setPrefillOptions: (options: { model: TModelType | null }) => void;
  setShouldPrefill: (shouldPrefill: boolean) => void;
  setErrors: (errors: ProjectErrors) => void;
  updateErrors: (updates: Partial<ProjectErrors>) => void;
  setIsLoading: (isLoading: boolean) => void;
  setProcessingCompleted: (val: boolean) => void;
  setDocumentSelections: (selections: TDocumentSelection[]) => void;
  clearForm: () => void;
  reset: () => void;
};

export const useCreateProjectStore = create<TCreateProjectState>()(
  persist(
    (set) => ({
      projectDataByCaseBrief: {},
      generatedMetaByCaseBrief: {},
      decisionFieldSuggestionsByCaseBrief: {},
      walkthroughByCaseBrief: {},
      launchNudgeSeenByCaseBrief: {},
      createdProjectId: null,
      prefillOptions: { model: "gpt-4.1" },
      shouldPrefill: false,
      errors: {},
      isLoading: false,
      processingCompleted: false,
      documentSelections: [],

      setProjectData: (caseBriefId, data) =>
        set((state) => ({
          projectDataByCaseBrief: { ...state.projectDataByCaseBrief, [caseBriefId]: data }
        })),

      updateProjectData: (caseBriefId, updates) =>
        set((state) => {
          const existing = state.projectDataByCaseBrief[caseBriefId] ?? initialProjectDataWithoutRepository;
          return {
            projectDataByCaseBrief: {
              ...state.projectDataByCaseBrief,
              [caseBriefId]: { ...existing, ...updates }
            }
          };
        }),

      clearProjectData: (caseBriefId) =>
        set((state) => {
          const without = <T,>(byCaseBrief: Record<number, T>) =>
            Object.fromEntries(Object.entries(byCaseBrief).filter(([id]) => Number(id) !== caseBriefId)) as Record<number, T>;
          return {
            projectDataByCaseBrief: without(state.projectDataByCaseBrief),
            generatedMetaByCaseBrief: without(state.generatedMetaByCaseBrief),
            decisionFieldSuggestionsByCaseBrief: without(state.decisionFieldSuggestionsByCaseBrief),
            walkthroughByCaseBrief: without(state.walkthroughByCaseBrief),
            launchNudgeSeenByCaseBrief: without(state.launchNudgeSeenByCaseBrief)
          };
        }),

      setGeneratedMeta: (caseBriefId, meta) =>
        set((state) => ({ generatedMetaByCaseBrief: { ...state.generatedMetaByCaseBrief, [caseBriefId]: meta } })),

      setDecisionFieldSuggestions: (caseBriefId, suggestions) =>
        set((state) => ({
          decisionFieldSuggestionsByCaseBrief: { ...state.decisionFieldSuggestionsByCaseBrief, [caseBriefId]: suggestions }
        })),

      setWalkthrough: (caseBriefId, walkthrough) =>
        set((state) => {
          const others = Object.fromEntries(
            Object.entries(state.walkthroughByCaseBrief).filter(([id]) => Number(id) !== caseBriefId)
          ) as Record<number, string>;
          return { walkthroughByCaseBrief: walkthrough === null ? others : { ...others, [caseBriefId]: walkthrough } };
        }),

      markLaunchNudgeSeen: (caseBriefId) =>
        set((state) => ({ launchNudgeSeenByCaseBrief: { ...state.launchNudgeSeenByCaseBrief, [caseBriefId]: true } })),

      setCreatedProjectId: (id) => set({ createdProjectId: id }),

      setPrefillOptions: (options) => set({ prefillOptions: options }),

      setShouldPrefill: (shouldPrefill) => set({ shouldPrefill }),

      setErrors: (errors) => set({ errors }),

      updateErrors: (updates) =>
        set((state) => ({
          errors: { ...state.errors, ...updates }
        })),

      setIsLoading: (isLoading) => set({ isLoading }),

      setProcessingCompleted: (val) => set({ processingCompleted: val }),

      setDocumentSelections: (selections) => set({ documentSelections: selections }),

      clearForm: () =>
        set({
          projectDataByCaseBrief: {},
          generatedMetaByCaseBrief: {},
          decisionFieldSuggestionsByCaseBrief: {},
          walkthroughByCaseBrief: {},
          launchNudgeSeenByCaseBrief: {},
          createdProjectId: null,
          prefillOptions: { model: "gpt-4.1" },
          shouldPrefill: false,
          errors: {},
          isLoading: false,
          processingCompleted: false,
          documentSelections: []
        }),

      reset: () =>
        set({
          projectDataByCaseBrief: {},
          generatedMetaByCaseBrief: {},
          decisionFieldSuggestionsByCaseBrief: {},
          walkthroughByCaseBrief: {},
          launchNudgeSeenByCaseBrief: {},
          createdProjectId: null,
          prefillOptions: { model: "gpt-4.1" },
          shouldPrefill: false,
          errors: {},
          isLoading: false,
          processingCompleted: false,
          documentSelections: []
        })
    }),
    {
      name: LS_CREATE_PROJECT_STORAGE,
      partialize: (state) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { isLoading, ...rest } = state;
        return rest;
      }
    }
  )
);

export function useProjectDataForCaseBrief(caseBriefId: number | null): TProjectDataWithoutRepository {
  return useCreateProjectStore((s) =>
    caseBriefId !== null
      ? s.projectDataByCaseBrief[caseBriefId] ?? initialProjectDataWithoutRepository
      : initialProjectDataWithoutRepository
  );
}

export const useCreateProjectFiles = () => {
  const [files, setFiles] = useState<File[]>([]);

  return {
    files,
    setFiles,
    addFiles: (newFiles: File[]) => setFiles((prev) => [...prev, ...newFiles]),
    removeFile: (fileToRemove: File) => setFiles((prev) => prev.filter((f) => f !== fileToRemove)),
    clearFiles: () => setFiles([])
  };
};
